import { useMemo, useState } from 'react'
import { AlertCircle, Delete, NotebookPen, Trash2 } from 'lucide-react'
import clsx from 'clsx'

import { Modal } from '@/componentes/ui/Modal'
import { plataCentavos, plataCorta } from '@/lib/formato'
import { LookupSelect } from '@/componentes/ui/LookupSelect'
import { useAyudaDe } from '@/componentes/ayuda/useAyudaDe'
import type { Cotizacion, MedioPagoPos, PagoTicket } from './tipos'
import { aplicaRedondeoEfectivo } from './redondeoEfectivo'
import { medioPorDefecto } from './medioPorDefecto'
import { useTeclaDelMedio } from './useTeclaDelMedio'
import { useEnterConfirma } from './useEnterConfirma'
import { useTecladoNumerico } from './useTecladoNumerico'
import { soltarElFoco, useTeclaAgregar } from './useTeclaAgregar'
import { pagoImplicito } from './pagoImplicito'

interface Props {
  /**
   * Lo que dice EL SERVIDOR que sale el ticket. No se recibe un total ya
   * armado porque el redondeo depende del medio de pago que el cajero está
   * por elegir: la cotización trae el total sin redondear y el ajuste que
   * aplicaría en efectivo, y acá se decide cuál corresponde.
   */
  cotizacion: Cotizacion

  medios: MedioPagoPos[]
  pagos: PagoTicket[]
  entregadoCentavos: number | null

  /** Cliente de cuenta corriente, cuando la venta se lleva fiada. */
  cliente: { id: number; nombre: string } | null

  onAgregarPago: (medio: MedioPagoPos, centavos: number, referencia?: string) => void
  onQuitarPago: (indice: number) => void
  onFijarEntregado: (centavos: number | null) => void
  onFijarCliente: (cliente: { id: number; nombre: string } | null) => void
  onCobrar: () => void
  onCerrar: () => void

  cobrando: boolean
  error?: string | null
}

// Cobro.
//
// Tres cosas que parecen detalles y son la diferencia entre un cobro de tres
// segundos y uno de treinta:
//
//   1. El importe arranca cargado con lo que FALTA. El caso mayoritario es un
//      solo medio de pago por el total, y así se resuelve con un toque.
//   2. Los billetes de uso corriente están como botones. El cajero no tipea
//      «10000», toca $10.000.
//   3. El vuelto se muestra grande y en verde antes de confirmar. Es el número
//      que el cajero le canta al cliente, y equivocarlo es plata de la caja.
//   4. Con UN SOLO medio el pago se da por entendido: no hay que apretar
//      «Agregar el pago» para que Cobrar se habilite. Ver `pagoImplicito`.
export function DialogoCobro({
  cotizacion,
  medios,
  pagos,
  entregadoCentavos,
  cliente,
  onAgregarPago,
  onQuitarPago,
  onFijarEntregado,
  onFijarCliente,
  onCobrar,
  onCerrar,
  cobrando,
  error,
}: Props) {
  useAyudaDe('pos.cobro')

  // El del local, no el primero de la lista. Ver `medioPorDefecto`.
  const [medio, setMedio] = useState<MedioPagoPos | null>(() => medioPorDefecto(medios))
  const [digitos, setDigitos] = useState('')
  const [referencia, setReferencia] = useState('')

  // La tecla rápida de cada medio, enganchada de verdad: se mostraba y nadie
  // la escuchaba. Ver `useTeclaDelMedio`.
  useTeclaDelMedio(medios, setMedio)

  const cobrado = useMemo(() => pagos.reduce((a, p) => a + p.importeCentavos, 0), [pagos])

  // El redondeo de efectivo solo aplica si TODO se paga con medios que dan
  // vuelto: redondear una transferencia dejaría la conciliación bancaria con
  // una diferencia por cada venta.
  //
  // La decisión se toma con los pagos ya cargados MÁS el que está por
  // cargarse, y eso es lo que hace que la pantalla y el servidor coincidan.
  // Antes se ignoraba el medio seleccionado en cuanto había un pago cargado, y
  // el resultado era un cobro RECHAZADO con el cliente adelante: con paso de
  // $100 y un total de $1.487, el cajero cargaba $1.000 en efectivo (la
  // pantalla redondeaba a $1.500 y pedía $500), elegía tarjeta para el resto,
  // y el servidor —que ve los dos medios— calculaba $1.487 y devolvía que los
  // pagos no cierran.
  //
  // Ojo con la circularidad: `falta` sale del total, el total sale del
  // redondeo y el redondeo sale de esta decisión. Por eso se mira el faltante
  // SIN redondeo, que no depende de ella: si lo cargado ya cubre eso, la lista
  // de pagos está completa y el medio seleccionado no va a entrar.
  const faltaSinRedondeo = Math.max(0, cotizacion.totalSinRedondeoCentavos - cobrado)

  const todoEfectivo = useMemo(
    () => aplicaRedondeoEfectivo(pagos, medio, faltaSinRedondeo),
    [pagos, medio, faltaSinRedondeo],
  )

  const redondeo = todoEfectivo ? cotizacion.redondeoSiEfectivoCentavos : 0
  const totalCentavos = cotizacion.totalSinRedondeoCentavos + redondeo

  const falta = Math.max(0, totalCentavos - cobrado)

  // El importe arranca en lo que falta: es el caso mayoritario.
  const importe = digitos === '' ? falta : Number(digitos) * 100

  // El reset de lo tipeado (para que el campo vuelva a mostrar el faltante
  // nuevo) se hace en los handlers que agregan y quitan pagos, y NO en un
  // efecto que observa el faltante.
  //
  // Con el efecto, cada pago disparaba un render de más solo para limpiar el
  // campo, y además borraba lo tipeado si el faltante cambiaba por cualquier
  // otro motivo. La regla es la de siempre: si algo pasa POR una acción, va en
  // la acción.
  const quitarPago = (indice: number) => {
    onQuitarPago(indice)
    setDigitos('')
  }

  const efectivo = medio?.daVuelto === true
  const vuelto =
    efectivo && entregadoCentavos !== null && entregadoCentavos >= totalCentavos
      ? entregadoCentavos - totalCentavos
      : null

  // El fiado necesita cliente: sin cliente la deuda no tiene dueño y no se
  // puede cobrar nunca. Se mira tanto lo ya cargado como el medio que está
  // seleccionado, así el combo aparece en cuanto el cajero toca «Fiado» y no
  // recién cuando ya agregó el pago.
  const hayFiado = pagos.some((p) => p.medio.esCuentaCorriente)
  const vaAFiar = hayFiado || medio?.esCuentaCorriente === true
  const faltaCliente = vaAFiar && cliente === null

  const puedeAgregar =
    medio !== null &&
    importe > 0 &&
    (!medio.pideReferencia || referencia.trim().length > 0) &&
    (!medio.esCuentaCorriente || cliente !== null)

  const agregar = () => {
    if (!puedeAgregar || !medio) return

    onAgregarPago(medio, importe, medio.pideReferencia ? referencia.trim() : undefined)
    setReferencia('')
    setDigitos('')

    // El botón que agregó el pago suelta el foco, o el Enter que viene después
    // vuelve a apretarlo en lugar de cobrar. Ver el helper.
    soltarElFoco()
  }

  // El pago que se da por entendido: un solo medio, por el total. La regla
  // está en su propio archivo y con tests porque decide el IMPORTE de un pago,
  // y ese número lo compara el servidor contra el total (ver pagoImplicito.ts).
  const implicito = pagoImplicito({
    pagos,
    medio,
    cliente,
    referencia,
    importeCentavos: importe,
    totalCentavos,
  })

  const listo =
    totalCentavos > 0 && !faltaCliente && (cobrado === totalCentavos || implicito !== null)

  const confirmar = () => {
    if (!listo || cobrando) return

    // El pago implícito se materializa recién acá, al cobrar: la venta que
    // viaja al servidor lleva sus pagos como cualquier otra, así que el host
    // no tiene que saber nada de esta comodidad de la pantalla.
    if (implicito) onAgregarPago(implicito.medio, implicito.importeCentavos, implicito.referencia)

    onCobrar()
  }

  // Enter cobra. Faltaba solo acá: los otros dos diálogos que cobran ya lo
  // tenían, así que el cobro del POS —el que más se usa— era el único donde
  // había que ir al mouse después de tipear con cuánto paga. El hook ignora la
  // tecla si el foco está en algo que el Enter activa por su cuenta (un botón,
  // un enlace), así que llegar con Tab a un medio de pago sigue eligiéndolo en
  // lugar de cobrar.
  useEnterConfirma(confirmar, { activo: listo && !cobrando })

  // Y «+» agrega el pago cargado, para el cobro dividido. Ver el hook.
  useTeclaAgregar(agregar, { activo: puedeAgregar })

  // El teclado de la MÁQUINA carga el importe del pago, igual que en los otros
  // dos diálogos que piden un número. Faltaba justo acá, que es el diálogo que
  // más se abre: el importe solo se podía cargar tocando el teclado de la
  // pantalla, así que el cobro DIVIDIDO —el único que necesita un importe
  // parcial— obligaba al mouse. Es el mismo error que el hook vino a resolver,
  // con el diálogo principal afuera de la lista.
  //
  // Con el foco en «¿Con cuánto paga?» los dígitos son de ese campo, que es lo
  // que corresponde y lo que ya se usaba: el hook deja pasar lo que se tipea en
  // un campo de verdad. Un Tab sale de ahí y desde ahí el teclado carga este
  // importe.
  useTecladoNumerico(setDigitos)

  return (
    <Modal
      onCerrar={onCerrar}
      titulo="Cobrar"
      ancho="lg"
      // El botón que cobra va en el PIE del Modal y no adentro del cuerpo.
      //
      // Estaba abajo de la columna derecha, que es parte de lo que scrollea:
      // en un teléfono, donde las dos columnas se apilan, «Cobrar $1.500,00»
      // quedaba 427 px abajo del pliegue y el cajero tenía que buscarlo
      // scrolleando el diálogo entero, con el cliente adelante. Medido en
      // Chromium a 390x844.
      //
      // Es exactamente el bug que el propio Modal ya resolvía —el pie es
      // `shrink-0` y el cuerpo se queda con lo que sobra—, y que esta pantalla
      // se estaba salteando por armar sus botones a mano.
      pie={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center">
          <button type="button" onClick={onCerrar} className="btn-secundario">
            Volver al ticket
          </button>

          <div className="hidden flex-1 sm:block" />

          <button
            type="button"
            onClick={confirmar}
            disabled={!listo || cobrando}
            className="btn-primario py-3.5 text-[16px] sm:min-w-[220px]"
          >
            {cobrando ? 'Registrando…' : `Cobrar ${plataCentavos(totalCentavos)}`}
          </button>
        </div>
      }
    >
      <div className="grid gap-5 md:grid-cols-[1fr_320px]">
        {/* Izquierda: medios y teclado */}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {medios.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setMedio(m)}
                className={clsx(
                  'flex min-h-[56px] flex-col items-start justify-center gap-0.5 rounded-xl border px-3 py-2 text-left transition',
                  medio?.id === m.id
                    ? 'border-verde bg-verde-tenue'
                    : 'border-borde bg-papel hover:border-borde-fuerte',
                )}
              >
                <span className="text-[14px] font-semibold text-tinta">{m.nombre}</span>

                {m.teclaRapida && (
                  <span className="num text-[11px] text-tinta-tenue">{m.teclaRapida}</span>
                )}
              </button>
            ))}
          </div>

          {/* El combo de cliente aparece solo cuando hace falta.
              Mostrarlo siempre le agregaría un campo a la pantalla que se usa
              cien veces por día para la venta que no lo necesita.

              Va pegado a los medios y ARRIBA del teclado por dos motivos: al
              fiar, a quién se le anota es la decisión principal (el importe
              viene precargado con el total), y abajo del teclado el
              desplegable del combo quedaba cortado por el borde del diálogo. */}
          {vaAFiar && (
            <div className="rounded-lg border border-ambar/50 bg-ambar/10 p-3">
              <p className="mb-2 flex items-center gap-2 text-[13px] font-semibold text-tinta">
                <NotebookPen className="h-4 w-4 text-ambar" />
                ¿A quién se le anota?
              </p>

              <LookupSelect
                fuente="clientes"
                valor={cliente?.id ?? null}
                onCambio={(id, item) =>
                  onFijarCliente(id && item ? { id, nombre: item.nombre } : null)
                }
                placeholder="Buscá por nombre o apodo"
                permiteVaciar
              />

              <p className="mt-1.5 text-[12px] text-tinta-suave">
                {cliente
                  ? `Se le va a cargar a la cuenta de ${cliente.nombre}.`
                  : 'Sin cliente la deuda no queda anotada en ninguna cuenta.'}
              </p>
            </div>
          )}

          <div className="rounded-xl border border-borde bg-crema-alt p-4 text-center">
            <p className="text-[12.5px] font-medium uppercase tracking-wide text-tinta-tenue">
              Importe de este pago
            </p>

            <p className="num mt-1 text-[34px] font-bold text-tinta">{plataCentavos(importe)}</p>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
              <Tecla key={d} onClick={() => setDigitos((v) => (v + d).slice(0, 9))}>
                {d}
              </Tecla>
            ))}

            <Tecla onClick={() => setDigitos((v) => v.slice(0, -1))} aria-label="Borrar">
              <Delete className="mx-auto h-5 w-5" />
            </Tecla>

            <Tecla onClick={() => setDigitos((v) => (v + '0').slice(0, 9))}>0</Tecla>

            <Tecla onClick={() => setDigitos('')} aria-label="Volver al faltante">
              C
            </Tecla>
          </div>

          {medio?.pideReferencia && (
            <div>
              <label
                htmlFor="referencia-pago"
                className="mb-1 block text-[13px] font-medium text-tinta-suave"
              >
                Número de {medio.esCuentaCorriente ? 'cuenta' : 'comprobante'}
              </label>

              <input
                id="referencia-pago"
                value={referencia}
                onChange={(e) => setReferencia(e.target.value)}
                placeholder="Cupón, lote o comprobante del QR"
                className="w-full rounded-lg border border-borde bg-papel px-3 py-2.5 text-[14px]
                           outline-none focus:border-verde"
              />

              <p className="mt-1 text-[12px] text-tinta-tenue">
                Es con lo que después se concilia contra el resumen.
              </p>
            </div>
          )}

          <div>
            <button
              type="button"
              onClick={agregar}
              disabled={!puedeAgregar}
              className="btn-secundario w-full"
            >
              Agregar el pago <span className="num opacity-70">+</span>
            </button>

            {/* Decir para qué es. Con el pago de un solo medio ya entendido,
                este botón quedó para el cobro DIVIDIDO, y sin el renglón se
                lee como un paso obligatorio que alguien se estaría
                salteando. */}
            <p className="mt-1.5 text-center text-[12px] text-tinta-tenue">
              Solo si paga con más de un medio: cargá el importe de cada uno.
            </p>
          </div>
        </div>

        {/* Derecha: lo que se lleva cobrado */}
        <div className="flex flex-col gap-3">
          <div className="rounded-xl border border-borde bg-papel p-4">
            {/* El redondeo se muestra SIEMPRE que exista, con su signo. Es lo
                que evita la discusión de mostrador: el cliente ve por qué el
                total no termina en el número que esperaba. */}
            {redondeo !== 0 && (
              <>
                <Renglon etiqueta="Subtotal" valor={cotizacion.totalSinRedondeoCentavos} />

                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[13px] uppercase tracking-wide text-tinta-tenue">
                    Redondeo
                  </span>

                  <span className="num text-[15px] text-tinta">
                    {redondeo > 0 ? '+' : '−'}
                    {plataCentavos(Math.abs(redondeo))}
                  </span>
                </div>
              </>
            )}

            <Renglon etiqueta="Total" valor={totalCentavos} fuerte />

            {pagos.length > 0 && (
              <ul className="mt-3 space-y-1.5 border-t border-borde pt-3">
                {pagos.map((p, i) => (
                  <li key={i} className="flex items-center justify-between gap-2 text-[13.5px]">
                    <span className="min-w-0 flex-1 truncate text-tinta-suave">
                      {p.medio.nombre}
                      {p.referencia && (
                        <span className="text-tinta-tenue"> ({p.referencia})</span>
                      )}
                    </span>

                    <span className="num font-medium text-tinta">
                      {plataCentavos(p.importeCentavos)}
                    </span>

                    <button
                      type="button"
                      onClick={() => quitarPago(i)}
                      aria-label={`Quitar el pago con ${p.medio.nombre}`}
                      className="grid h-7 w-7 place-items-center rounded text-tinta-tenue hover:bg-rojo/10 hover:text-rojo"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-3 border-t border-borde pt-3">
              {/* Con el pago entendido, un «FALTA» en rojo por el total sería
                  el sistema contradiciendo al botón que está habilitado justo
                  al lado. Se dice con QUÉ se va a cobrar, que además es la
                  confirmación de que el medio elegido es el correcto. */}
              <Renglon
                etiqueta={
                  implicito ? `Con ${implicito.medio.nombre}` : falta > 0 ? 'Falta' : 'Cobrado'
                }
                valor={implicito ? implicito.importeCentavos : falta > 0 ? falta : cobrado}
                acento={!implicito && falta > 0 ? 'rojo' : 'verde'}
                fuerte
              />
            </div>
          </div>

          {/* Efectivo: entregado y vuelto */}
          {efectivo && (
            <div className="rounded-xl border border-borde bg-papel p-4">
              <label
                htmlFor="entregado"
                className="mb-1.5 block text-[13px] font-medium text-tinta-suave"
              >
                ¿Con cuánto paga?
              </label>

              <input
                id="entregado"
                inputMode="numeric"
                value={entregadoCentavos !== null ? String(entregadoCentavos / 100) : ''}
                onChange={(e) => {
                  const n = Number(e.target.value.replace(/\D/g, ''))
                  onFijarEntregado(Number.isFinite(n) && n > 0 ? n * 100 : null)
                }}
                placeholder="Opcional"
                className="num w-full rounded-lg border border-borde bg-crema-alt px-3 py-2.5
                           text-right text-[18px] outline-none focus:border-verde"
              />

              {/* Billetes de uso corriente. El cajero toca, no tipea. */}
              <div className="mt-2 flex flex-wrap gap-1.5">
                {[1000, 2000, 5000, 10000, 20000].map((b) => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => onFijarEntregado(b * 100)}
                    className="num rounded-lg border border-borde px-2.5 py-1.5 text-[12.5px]
                               text-tinta-suave hover:border-verde"
                  >
                    {plataCorta(b)}
                  </button>
                ))}
              </div>

              {vuelto !== null && (
                <div className="mt-3 rounded-lg bg-verde-tenue px-3 py-2.5">
                  <p className="text-[12.5px] font-medium uppercase tracking-wide text-verde-oscuro">
                    Vuelto
                  </p>

                  <p className="num text-[28px] font-bold text-verde-oscuro">
                    {plataCentavos(vuelto)}
                  </p>
                </div>
              )}

              {entregadoCentavos !== null && entregadoCentavos < totalCentavos && (
                <p className="mt-2 flex items-start gap-1.5 text-[12.5px] text-ambar">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  Con eso no alcanza para cubrir el total.
                </p>
              )}
            </div>
          )}

          {error && (
            <p className="flex items-start gap-2 rounded-lg bg-rojo/10 px-3 py-2.5 text-[13px] text-rojo">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {error}
            </p>
          )}

        </div>
      </div>
    </Modal>
  )
}

function Renglon({
  etiqueta,
  valor,
  fuerte,
  acento,
}: {
  etiqueta: string
  valor: number
  fuerte?: boolean
  acento?: 'rojo' | 'verde'
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span
        className={clsx(
          'text-[13px] uppercase tracking-wide',
          acento === 'rojo' ? 'text-rojo' : acento === 'verde' ? 'text-verde-oscuro' : 'text-tinta-tenue',
        )}
      >
        {etiqueta}
      </span>

      <span
        className={clsx(
          'num',
          fuerte ? 'text-[22px] font-bold' : 'text-[15px]',
          acento === 'rojo' ? 'text-rojo' : acento === 'verde' ? 'text-verde-oscuro' : 'text-tinta',
        )}
      >
        {plataCentavos(valor)}
      </span>
    </div>
  )
}

function Tecla({
  onClick,
  children,
  ...resto
}: { onClick: () => void; children: React.ReactNode } & React.ComponentProps<'button'>) {
  return (
    <button
      type="button"
      onClick={onClick}
      {...resto}
      // 48 px de alto. Menos que las teclas del PIN (que son de 64) porque
      // acá el teclado comparte pantalla con el resumen del cobro, y más que
      // eso lo empuja abajo del pliegue en un monitor de caja.
      className="num h-12 rounded-xl border border-borde bg-papel text-[19px] font-semibold
                 text-tinta transition hover:border-verde active:scale-95"
    >
      {children}
    </button>
  )
}
